/**
 * EsgKit — shared presentational kit for the e136e2e5 regulatory pages
 * (ESRS E2/E3/E4/S2/S3/S4, climate scenarios, SFDR funds, materiality
 * thresholds, minimum safeguards).
 *
 * Idiom lifted from pages/frameworks/CBAMModule.jsx, the design-reviewed
 * exemplar: DESIGN.md tokens as inline styles, JetBrains Mono for every
 * data value, status always color + icon + label (auditors print in
 * grayscale), flat 1px-bordered cards on deep obsidian.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Inbox, AlertTriangle, ShieldCheck } from "lucide-react";
import { clearSession } from "../../services/api_service";

// ─── DESIGN.md tokens (Command Center / Defensibility Spine) ───────────────────
export const C = {
  bg: "#0A0A0A", surface: "#171717", surfaceHi: "#1F1F22", border: "#27272A",
  primary: "#10B981", accent: "#F59E0B", destructive: "#EF4444", locked: "#3B82F6",
  // muted is TEXT (th headers, units, empty labels) — must clear 4.5:1 on
  // surface (#8A8A94 = 4.9:1; the old #52525B sat at 2.32:1). Purely
  // decorative strokes keep using C.border, not C.muted.
  ink: "#FAFAFA", body: "#A1A1AA", muted: "#8A8A94", neutral: "#8A8A94",
};
export const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace";
export const SANS = "'Inter', system-ui, -apple-system, sans-serif";
export const HEAD = "'Outfit', 'Inter', system-ui, sans-serif";

// Badge tones — semantic vocabulary shared across all ESG pages.
export const TONES = {
  emerald: C.primary, amber: C.accent, red: C.destructive,
  blue: C.locked, zinc: C.neutral,
};

const API_BASE =
  typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL
    ? process.env.REACT_APP_BACKEND_URL
    : "";

export async function apiFetch(path, opts = {}) {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  const r = await fetch(`${API_BASE}${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (r.status === 401) {
    // Align with services/api_service.js: clear the stale session and avoid a
    // redirect loop when already on /login. Still throw so page try/catch runs.
    clearSession();
    if (!window.location.pathname.startsWith("/login")) {
      const dest = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(dest)}`;
    }
    throw new Error("Session expired");
  }
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const j = await r.json();
      detail = j?.detail || detail;
      if (Array.isArray(detail)) detail = detail.map((d) => d?.msg || "").join("; ");
    } catch { /* non-JSON error body */ }
    const err = new Error(typeof detail === "string" ? detail : "Request failed");
    err.status = r.status; // typed states (permission vs error) need the code
    throw err;
  }
  return r.json();
}

// ─── Formatting (data always in mono) ──────────────────────────────────────────
export const fmt = (n, d = 2) =>
  n === null || n === undefined || n === "" || isNaN(Number(n))
    ? "—"
    : Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: d,
      });

export const Data = ({ children, color, size = 12, weight = 600 }) => (
  <span style={{ fontFamily: MONO, fontSize: size, fontWeight: weight, color: color || C.ink }}>
    {children}
  </span>
);

// ─── Badge: color + icon + label, never color alone ────────────────────────────
export const Badge = ({ c, Icon, label, title }) => (
  <span
    title={title}
    style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
      borderRadius: 999, fontFamily: MONO, fontSize: 11, fontWeight: 600,
      color: c, background: `${c}1A`, border: `1px solid ${c}40`, whiteSpace: "nowrap",
    }}
  >
    {Icon && <Icon size={12} strokeWidth={1.5} />} {label}
  </span>
);

// ─── Data quality: what reference data a figure rests on ───────────────────────
// A verdict computed against an illustrative default must never read as an
// authoritative pass. `authoritative` is the backend's machine-readable lineage
// gate (E2 limits_authoritative, NGFS carbon_prices_authoritative, E4
// msa_factor_source); the note carries the regulatory caveat as a tooltip.
// Single vocabulary — amber + AlertTriangle for illustrative, blue + ShieldCheck
// for authoritative — so the same distinction never renders three ways.
export const DataQualityBadge = ({ authoritative, label, note }) => {
  const ok = authoritative === true;
  return (
    <Badge
      c={ok ? C.locked : C.accent}
      Icon={ok ? ShieldCheck : AlertTriangle}
      label={label || (ok ? "Authoritative" : "Illustrative")}
      title={note}
    />
  );
};

// GLOBIO 3.5 publishes MSA factors for four land-use classes; the rest are
// approximations or expert placeholders, flagged per impact by the E4 service.
export const MSA_SOURCE = {
  "globio_3.5": { authoritative: true, label: "GLOBIO 3.5",
    note: "MSA factor published in GLOBIO 3.5 for this land-use class." },
  globio_approx: { authoritative: false, label: "GLOBIO approx.",
    note: "Approximated from the nearest GLOBIO 3.5 class — not a published factor." },
  placeholder: { authoritative: false, label: "Placeholder",
    note: "Expert placeholder — GLOBIO 3.5 has no factor for this class. Not assurance-ready." },
};

export const Card = ({ children, style, accent, ...rest }) => (
  <section
    {...rest}
    style={{
      background: C.surface, border: `1px solid ${accent ? `${accent}55` : C.border}`,
      borderRadius: 8, padding: 20, ...style,
    }}
  >
    {children}
  </section>
);

export const Overline = ({ children, Icon, color = C.body, style }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 7, marginBottom: 14,
      fontFamily: HEAD, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
      textTransform: "uppercase", color, ...style,
    }}
  >
    {Icon && <Icon size={14} strokeWidth={1.5} />} {children}
  </div>
);

export const Btn = ({ children, onClick, variant = "primary", disabled, type = "button", Icon, title }) => {
  const styles = {
    primary: { bg: C.primary, fg: "#0A0A0A", bd: C.primary, glow: `0 0 10px ${C.primary}33` },
    secondary: { bg: "transparent", fg: C.ink, bd: C.border, glow: "none" },
    destructive: { bg: `${C.destructive}20`, fg: C.destructive, bd: `${C.destructive}80`, glow: "none" },
    ghost: { bg: "transparent", fg: C.body, bd: "transparent", glow: "none" },
  }[variant];
  return (
    <button
      type={type} onClick={onClick} disabled={disabled} title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px",
        borderRadius: 6, fontFamily: SANS, fontSize: 13, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        color: styles.fg, background: styles.bg, border: `1px solid ${styles.bd}`,
        boxShadow: styles.glow, opacity: disabled ? 0.45 : 1,
        transition: "background .15s, border-color .15s",
      }}
      onMouseEnter={(e) => { if (!disabled && variant === "secondary") e.currentTarget.style.borderColor = `${C.primary}80`; }}
      onMouseLeave={(e) => { if (variant === "secondary") e.currentTarget.style.borderColor = C.border; }}
    >
      {Icon && <Icon size={14} strokeWidth={1.5} />} {children}
    </button>
  );
};

// ─── Form primitives (inputs mono per DESIGN.md) ───────────────────────────────
// No inline `outline: none` — the Page stylesheet owns focus treatment
// (inline outline would beat the :focus-visible ring on specificity and
// leave keyboard focus invisible, WCAG 2.4.7).
export const field = {
  width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.bg, color: C.ink, fontFamily: MONO, fontSize: 13, boxSizing: "border-box",
};
export const Label = ({ children }) => (
  <label style={{ display: "block", fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 4 }}>
    {children}
  </label>
);

// ─── KPI metric ─────────────────────────────────────────────────────────────────
export const Metric = ({ label, value, unit, color }) => (
  <div>
    <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>{label}</div>
    <Data size={22} color={color || C.ink}>{value}</Data>
    {unit && <span style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}> {unit}</span>}
  </div>
);

// ─── Tabs ───────────────────────────────────────────────────────────────────────
export const TabBar = ({ tabs, active, onChange }) => (
  <div role="tablist" style={{ display: "flex", gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 16, flexWrap: "wrap" }}>
    {tabs.map((t) => {
      const is = t.id === active;
      return (
        <button
          key={t.id} onClick={() => onChange(t.id)}
          role="tab" aria-selected={is}
          style={{
            padding: "8px 14px", background: "transparent", border: "none", cursor: "pointer",
            fontFamily: SANS, fontSize: 13, fontWeight: 600,
            color: is ? C.primary : C.body,
            borderBottom: `2px solid ${is ? C.primary : "transparent"}`,
            marginBottom: -1, transition: "color .15s, border-color .15s",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          {t.label}
          {t.count !== undefined && (
            <span style={{ fontFamily: MONO, fontSize: 10, color: is ? C.primary : C.muted }}>{t.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

// ─── States ─────────────────────────────────────────────────────────────────────
export const ErrorBanner = ({ message, onDismiss }) =>
  message ? (
    <div
      role="alert"
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
        padding: "10px 14px", borderRadius: 6, marginBottom: 16,
        background: `${C.destructive}12`, border: `1px solid ${C.destructive}50`,
        fontFamily: MONO, fontSize: 12, color: C.destructive,
      }}
    >
      <span>{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{ background: "none", border: "none", color: C.destructive, cursor: "pointer", fontFamily: MONO, fontSize: 12 }}>
          ✕
        </button>
      )}
    </div>
  ) : null;

export const EmptyState = ({ Icon = Inbox, title, body, children }) => (
  <div style={{ textAlign: "center", padding: "28px 20px", color: C.body }}>
    <Icon size={28} strokeWidth={1.25} color={C.muted} aria-hidden="true" />
    <div style={{ fontFamily: HEAD, fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 10 }}>{title}</div>
    <div style={{ fontFamily: SANS, fontSize: 13, color: C.body, maxWidth: 460, margin: "6px auto 0", lineHeight: 1.5 }}>{body}</div>
    {children && <div style={{ marginTop: 14 }}>{children}</div>}
  </div>
);

export const Skeleton = ({ h = 120 }) => (
  // Shimmer styles live in the Page stylesheet (one copy per page, not per
  // skeleton instance).
  <div aria-hidden style={{ height: h, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, position: "relative", overflow: "hidden" }}>
    <div className="esg-shimmer" style={{ position: "absolute", inset: 0 }} />
  </div>
);

// ─── Data table (mono cells, CBAM idiom) ────────────────────────────────────────
export const th = {
  textAlign: "left", padding: "8px 10px", fontFamily: SANS, fontSize: 11, fontWeight: 600,
  color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
};
export const td = { padding: "9px 10px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" };

export const DataTable = ({ columns, rows, keyField, emptyLabel = "No records" }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key} scope="col" style={{ ...th, textAlign: col.align || "left" }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ ...td, fontFamily: MONO, fontSize: 12, color: C.muted, textAlign: "center", borderBottom: "none" }}>
              {emptyLabel}
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row[keyField]}>
              {columns.map((col) => (
                <td key={col.key} style={{ ...td, textAlign: col.align || "left" }}>
                  {col.render ? col.render(row) : <Data color={C.body}>{row[col.key] ?? "—"}</Data>}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ─── Page header with entity scope ──────────────────────────────────────────────
export const PageHeader = ({ overline, OverlineIcon, title, subtitle, entities, entityId, onEntityChange, onRefresh, loading, actions }) => (
  <header style={{ marginBottom: 20 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
      <div>
        <Overline Icon={OverlineIcon} style={{ marginBottom: 6 }}>{overline}</Overline>
        <h1 style={{ fontFamily: HEAD, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: C.ink, margin: 0 }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: "6px 0 0", maxWidth: "70ch", lineHeight: 1.5 }}>
            {subtitle}
          </p>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {actions}
        {entities && (
          <select
            value={entityId || ""} onChange={(e) => onEntityChange(e.target.value)}
            aria-label="Reporting entity"
            style={{ ...field, width: "auto", minWidth: 180 }}
          >
            <option value="" disabled>Select entity…</option>
            {entities.map((en) => (
              <option key={en.entity_id} value={en.entity_id}>{en.name || en.entity_id}</option>
            ))}
          </select>
        )}
        {onRefresh && (
          <Btn variant="secondary" Icon={RefreshCw} onClick={onRefresh} disabled={loading} title="Refresh">
            {loading ? "Loading…" : "Refresh"}
          </Btn>
        )}
      </div>
    </div>
  </header>
);

// ─── Page shell: obsidian bg + DESIGN.md container padding ─────────────────────
// Owns the one shared stylesheet: keyboard focus ring (WCAG 2.4.7 — inline
// styles can't express :focus-visible) and the skeleton shimmer keyframes.
export const Page = ({ children }) => (
  <div className="esg-page" style={{ minHeight: "100vh", background: C.bg, padding: "24px 24px 48px" }}>
    <style>{`
      .esg-page :is(input, select, textarea, button):focus-visible {
        outline: 2px solid ${C.primary}CC; outline-offset: 1px;
      }
      .esg-shimmer { background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.04) 50%, transparent 70%); background-size: 200% 100%; animation: esgShimmer 1.4s ease-out infinite; }
      @keyframes esgShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
      @media (prefers-reduced-motion: reduce) { .esg-shimmer { animation: none; } }
    `}</style>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>{children}</div>
  </div>
);
